import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { useEffect } from 'react';
import {
  $getNearestNodeFromDOMNode,
  $getSelection,
  $isRangeSelection,
  $createParagraphNode,
  $createTextNode,
  KEY_ENTER_COMMAND,
  KEY_BACKSPACE_COMMAND,
  COMMAND_PRIORITY_HIGH,
} from 'lexical';
import { $isChecklistItemNode, $createChecklistItemNode } from '../nodes/ChecklistItemNode';

export default function ChecklistPlugin() {
  const [editor] = useLexicalComposerContext();

  // Handle checkbox click toggle
  useEffect(() => {
    const handleClick = (event) => {
      const target = event.target;

      if (target.classList.contains('checklist-item')) {
        const rect = target.getBoundingClientRect();
        const clickX = event.clientX - rect.left;

        // Click within first 30px = checkbox area
        if (clickX <= 30) {
          event.preventDefault();
          editor.update(() => {
            const node = $getNearestNodeFromDOMNode(target);
            if ($isChecklistItemNode(node)) {
              node.toggleChecked();
            }
          });
        }
      }
    };

    const editorElement = editor.getRootElement();
    if (editorElement) {
      editorElement.addEventListener('click', handleClick);
    }
    return () => {
      if (editorElement) {
        editorElement.removeEventListener('click', handleClick);
      }
    };
  }, [editor]);

  // Handle Enter key — create new checklist item
  useEffect(() => {
    return editor.registerCommand(
      KEY_ENTER_COMMAND,
      (event) => {
        const selection = $getSelection();
        if (!$isRangeSelection(selection) || !selection.isCollapsed()) return false;

        const anchorNode = selection.anchor.getNode();
        let checklistItemNode = anchorNode;

        // Walk up to find the ChecklistItemNode
        while (checklistItemNode && !$isChecklistItemNode(checklistItemNode)) {
          checklistItemNode = checklistItemNode.getParent();
        }

        if (!$isChecklistItemNode(checklistItemNode)) return false;

        event?.preventDefault?.();

        const textContent = checklistItemNode.getTextContent();

        // If empty checklist item → convert to plain paragraph
        if (textContent === '') {
          const paragraph = $createParagraphNode();
          checklistItemNode.replace(paragraph);
          paragraph.select();
          return true;
        }

        // Split text at cursor position
        const anchor = selection.anchor;
        const offset = anchor.offset;
        const currentTextNode = anchor.getNode();

        // Create new checklist item
        const newItem = $createChecklistItemNode(false);
        const newParagraph = $createParagraphNode();
        newItem.append(newParagraph);

        // If cursor is at the end of the text, just create empty new item
        if (currentTextNode.getType() === 'text') {
          const text = currentTextNode.getTextContent();
          if (offset < text.length) {
            // Split: move text after cursor to new item
            const afterText = text.substring(offset);
            currentTextNode.setTextContent(text.substring(0, offset));
            newParagraph.append($createTextNode(afterText));
          }
        }

        checklistItemNode.insertAfter(newItem);
        newParagraph.selectStart();

        return true;
      },
      COMMAND_PRIORITY_HIGH,
    );
  }, [editor]);

  // Handle Backspace — remove empty checklist items
  useEffect(() => {
    return editor.registerCommand(
      KEY_BACKSPACE_COMMAND,
      (event) => {
        const selection = $getSelection();
        if (!$isRangeSelection(selection) || !selection.isCollapsed()) return false;

        const anchor = selection.anchor;
        if (anchor.offset !== 0) return false;

        const anchorNode = anchor.getNode();
        let checklistItemNode = anchorNode;

        while (checklistItemNode && !$isChecklistItemNode(checklistItemNode)) {
          checklistItemNode = checklistItemNode.getParent();
        }

        if (!$isChecklistItemNode(checklistItemNode)) return false;

        const textContent = checklistItemNode.getTextContent();

        if (textContent === '') {
          event?.preventDefault?.();

          // Convert to paragraph or select previous sibling
          const prevSibling = checklistItemNode.getPreviousSibling();
          if (prevSibling) {
            checklistItemNode.remove();
            prevSibling.selectEnd();
          } else {
            // First item — convert to paragraph
            const paragraph = $createParagraphNode();
            checklistItemNode.replace(paragraph);
            paragraph.select();
          }

          return true;
        }

        // If at the start of a non-empty checklist item, merge with previous
        if (anchor.offset === 0) {
          const prevSibling = checklistItemNode.getPreviousSibling();
          if (prevSibling && $isChecklistItemNode(prevSibling)) {
            event?.preventDefault?.();

            // Merge text into previous item
            const prevParagraph = prevSibling.getFirstChild();
            if (prevParagraph) {
              const currentParagraph = checklistItemNode.getFirstChild();
              if (currentParagraph) {
                const children = currentParagraph.getChildren();
                const insertPoint = prevParagraph.getLastChild();
                children.forEach((child) => {
                  if (insertPoint) {
                    insertPoint.insertAfter(child);
                  } else {
                    prevParagraph.append(child);
                  }
                });
              }
            }
            checklistItemNode.remove();
            prevSibling.selectEnd();
            return true;
          }
        }

        return false;
      },
      COMMAND_PRIORITY_HIGH,
    );
  }, [editor]);

  return null;
}
